import { DOCUMENT } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Inject,
  LOCALE_ID,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CoreService } from '@app/@core/services/core.service';
import { LanguageService } from '@app/@core/services/language.service';
import { I18nService } from '@app/i18n';
import { finalize } from 'rxjs/operators';
import { PaymentService } from './payment.service';
import {
  ILinkInformation,
  IPaymentInfo,
  PaymentGatewayTypes,
} from './payment.types';
import { environment } from '@env/environment';
import { FunnelAnalyticsService } from '@app/ahoy-analytics/funnel-analytics.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  providers: [
    {
      provide: LOCALE_ID,
      deps: [I18nService],
      useFactory: (i18nService: I18nService) => {
        return i18nService.language;
      },
    },
  ],
  standalone: false,
})
export class PaymentComponent implements OnInit, AfterViewChecked {
  @ViewChild('paymentForm') paymentForm: ElementRef;
  @ViewChild('paymentCashinForm') paymentCashinForm: ElementRef;
  isLoading: boolean = false;
  isCashinLoading = false;
  isRedirecting: boolean = false;
  paymentDetails: ILinkInformation;
  paymentCashinDetails: ILinkInformation;
  paymentInfo: IPaymentInfo;
  paymentId: string;
  error: string;
  selectedLanguage: string;
  dir: string;
  enableApplePay = environment.enableApplePay;
  amount: number;

  constructor(
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private i18nService: I18nService,
    private languageService: LanguageService,
    private funnelAnalytics: FunnelAnalyticsService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.selectedLanguage =
      this.route.snapshot.queryParamMap.get('lang') ||
      localStorage.getItem('language') ||
      'en-US';
    this.i18nService.language = this.selectedLanguage;
    this.languageService.language = this.selectedLanguage;
    this.changeHtmlDirection(this.selectedLanguage);

    this.paymentId = this.route.snapshot.params['id'];
    if (!this.paymentId) return;
    // this.getPaymentDetails();
    this.getPaymentInfo();
  }

  ngAfterViewChecked() {
    if (this.paymentForm && this.paymentDetails && !window.ApplePaySession) {
      //Commented out form now, might be needed again
      // this.paymentForm.nativeElement.submit();
    }
  }

  getPaymentInfo() {
    this.isLoading = true;
    this.isCashinLoading = true;
    this.paymentService
      .getPaymentInfo(this.paymentId, this.selectedLanguage)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isCashinLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res: IPaymentInfo) => {
          this.paymentInfo = res;
          this.amount = Number(res.amount);
          if (
            res.gateways?.some(
              (gateway) =>
                gateway.paymentGatewayType == PaymentGatewayTypes.Payfort,
            )
          ) {
            this.isLoading = true;
            this.getPaymentDetails()
              .pipe(
                finalize(() => {
                  this.isLoading = false;
                }),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe(
                (res: ILinkInformation) => {
                  this.paymentDetails = res;
                },
                (err) => {
                  if (
                    err.error?.error == 'No payment pending for this booking'
                  ) {
                    this.error = 'No payment pending for this booking';
                  } else {
                    this.coreService.showErrorDialog(err);
                  }
                },
              );
          }

          if (
            res.gateways?.some(
              (gateway) =>
                gateway.paymentGatewayType == PaymentGatewayTypes.Cashin,
            )
          ) {
            this.onCardTypeChange();
          }
        },
        (err) => {
          this.coreService.showErrorDialog(err);
        },
      );
  }

  getPaymentDetails() {
    return this.paymentService.getPaymentDetails(this.paymentId);
  }

  onCardTypeChange() {
    this.isCashinLoading = true;
    this.paymentService
      .getCashinPaymentDetails(this.paymentId)
      .pipe(
        finalize(() => {
          this.isCashinLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res: ILinkInformation) => {
          this.paymentCashinDetails = res;
        },
        (err) => {
          this.coreService.showErrorDialog(err);
        },
      );
  }

  submit() {
    if (this.paymentForm && this.paymentDetails) {
      this.funnelAnalytics.paymentInit(
        'payment',
        'payfort',
        this.paymentDetails.amount,
        this.paymentDetails.currency,
      );
      this.isRedirecting = true;
      this.paymentForm.nativeElement.submit();
    }
  }

  submitCashin() {
    if (this.paymentCashinDetails) {
      this.funnelAnalytics.paymentInit(
        'payment',
        'cashin',
        this.paymentCashinDetails.amount,
        this.paymentCashinDetails.currency,
      );
      this.isRedirecting = true;
      // this.paymentCashinForm.nativeElement.submit();
      window.location.href = this.paymentCashinDetails?.formActionURL;
    }
  }

  navigateToDashboard() {
    window.location.href = '/dashboard';
  }

  changeLanguage(event: { value: string }) {
    this.selectedLanguage = event.value;
    this.switchLanguage();
  }

  switchLanguage() {
    this.i18nService.language = this.selectedLanguage;
    this.languageService.language = this.selectedLanguage;
    if (this.selectedLanguage !== this.document.dir) {
      this.changeHtmlDirection(this.selectedLanguage);
    }
    location.reload();
  }

  get currentLanguage(): string {
    return this.i18nService.language;
  }

  changeHtmlDirection(lang: string) {
    let htmlTag = this.document.getElementsByTagName(
      'html',
    )[0] as HTMLHtmlElement;
    this.dir = lang === 'ar' ? 'rtl' : 'ltr';
    htmlTag.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('html-dir', this.dir);
  }
  get isHoldExpired(): boolean {
    const ts = this.paymentInfo?.slotReservationExpiresAtTimestamp;
    if (!ts) return false;
    return Date.now() >= ts;
  }

  get holdExpiresAtMs(): number | null {
    return this.paymentInfo?.slotReservationExpiresAtTimestamp ?? null;
  }
}
